import { type DeviceInputState, type DeviceRecordBase } from "@njbsoft/device-input/core";
import { type components } from "@njbsoft/types/field";
import { type components as mainComponents } from "@njbsoft/types";
import { type BlockedRequirement, type OutboxState } from "./outbox-state";

// Wire aliases. The main spec carries structurally identical schemas
// (transport parity by design) — the field-spec types are the canonical
// on-device vocabulary for both transports.
export type FieldworkBundle = components["schemas"]["FieldworkBundle"];
export type BundleWorkAssignment = components["schemas"]["WorkAssignment"];
export type BundleTemplateVersion = components["schemas"]["FieldworkBundleTemplateVersion"];
export type BundleTarget = components["schemas"]["FieldworkBundleTarget"];
export type WorkAssignmentStatus = components["schemas"]["WorkAssignmentStatus"];
export type FormSubmissionCreate = components["schemas"]["FormSubmissionCreate"];
export type FormSubmissionGps = components["schemas"]["FormSubmissionGps"];

// The change-request vocabulary the outbox, the sidecar and the bundle stores
// hold verbatim (field stack only).
export type ChangeRequestSetCreate = components["schemas"]["ChangeRequestSetCreate"];
export type ChangeRequestItemCreate = components["schemas"]["ChangeRequestItemCreate"];
export type ChangeRequestKind = components["schemas"]["ChangeRequestKind"];
export type ChangeRequestRejectionReason = components["schemas"]["ChangeRequestRejectionReason"];
export type ChangeRequestReceipt = components["schemas"]["ChangeRequestReceipt"];
export type ChangeRequestEvidenceFailure = components["schemas"]["ChangeRequestEvidenceFailure"];
export type ChangeRequestAttachmentKind = components["schemas"]["ChangeRequestAttachmentKind"];
export type ChangeRequestCategoryTemplate = components["schemas"]["ChangeRequestCategoryTemplate"];
export type ChangeRequestSet = components["schemas"]["ChangeRequestSet"];
export type ChangeRequestEvidence = components["schemas"]["ChangeRequestEvidence"];
export type ChangeRequestPendingNewInstallations =
  components["schemas"]["ChangeRequestPendingNewInstallations"];
export type DeviceOfRecordAttestation = components["schemas"]["DeviceOfRecordAttestation"];
export type AttachedChangeRequests = components["schemas"]["AttachedChangeRequests"];
export type ChangeRequestAttachmentUploadResult =
  components["schemas"]["ChangeRequestAttachmentUploadResult"];
export type IntakeCandidate = components["schemas"]["IntakeCandidate"];
export type IntakeCandidatesRequest = components["schemas"]["IntakeCandidatesRequest"];
export type IntakeFacts = components["schemas"]["IntakeFacts"];
export type CatalogSnapshot = components["schemas"]["CatalogSnapshot"];
export type FieldFacilityDeviceRow = components["schemas"]["FieldFacilityDeviceRow"];

// One violation an inspection found, held verbatim by the sidecar and the
// queued create (field stack; the main schema is structurally identical).
export type ViolationProposalCreate = components["schemas"]["ViolationProposalCreate"];

// The walk-up assignment-create body rides the MAIN spec — walk-up creation is
// org-mode-only, so the outbox row always replays on the main stack. The
// optional client-minted `id` makes a replayed create idempotent (201 create /
// 200 replay — both `synced`).
export type WorkAssignmentCreateBody = mainComponents["schemas"]["WorkAssignmentCreate"];

// The bundle kit arm. The wire schema carries no owner_label; ownership is
// owner_kind + owner_company_id / owner_company_name / owner_org_id, and the
// calibration dates are nested under latest_calibration?.{calibrated_at,
// calibration_expires_at}. deriveKitOwnerLabel + the StoredKit mapping flatten
// both at the replaceBundle insert.
export type BundleTestKit = components["schemas"]["BundleTestKit"];

// The single self-contained supplement arm of the bundle: routing metadata
// (company_id / company_name / target_kind / device_category) co-located with
// the pinned company-private template version (template_version_id + the
// definition document). There is no separate supplement-version array — the
// definition travels inside this row.
export type BundleSupplementTemplate = components["schemas"]["FieldworkBundleSupplementTemplate"];

// The grant-scoped facility snapshot arm: every facility the caller's RLS
// admits, each carrying its server-resolved read-source context map
// (read_context, stored verbatim). Optional address members are string |
// undefined on the wire; StoredFacility normalizes them to string | null.
export type BundleFacility = components["schemas"]["FieldworkBundleFacility"];

// A premise template startable as a walk-up (org home only; the field wire
// contract sends an empty array). Carries no org_id — the store binds the org
// from the pull scope.
export type BundleWalkupTemplate = components["schemas"]["FieldworkBundleWalkupTemplate"];

// The bundle's readiness arm: one block per org the caller holds a non-revoked
// grant with, evaluated at pull time. Empty on the org home. StoredUtility-
// Requirements is its on-device flattening.
export type BundleUtilityReadiness = components["schemas"]["UtilityReadiness"];

// Which API stack an outbox row replays against. Snapshotted at enqueue so
// an org switch (or a mode switch) never re-routes queued regulatory data.
export type SyncStack = "field" | "main";

// Bundle replacement scope: field mode wholesale-replaces the entire store
// (the field bundle is cross-org); org mode replaces only the active org's
// rows so another org's offline work survives.
export type BundleScope = { kind: "field" } | { kind: "org"; orgId: string };

// Which bundle a run pulls. Field scope is cross-org and wholesale-replaces the
// whole snapshot; org scope replaces only that org's rows so another org's
// offline work survives.
export type SyncScope = { mode: "field" } | { mode: "org"; orgId: string };

export interface StoredAssignment {
  assignment: BundleWorkAssignment;
  // Optimistic status set when a transition is queued offline; cleared by
  // the next pull (server truth wins) or by the sync engine on 409/404.
  localStatus: WorkAssignmentStatus | null;
}

export interface StoredTemplateVersion {
  id: string;
  templateId: string;
  version: number;
  title: string;
  targetKind: string;
  deviceCategory: string | null;
  reviewRequired: boolean;
  // The received definition serialized exactly once at store time — the
  // renderer parses THIS string (the single on-device representation).
  definitionJson: string;
}

// The on-device kit row, flattened from BundleTestKit. ownerLabel is derived
// at the insert (the wire has no owner_label); the calibration dates come from
// the nested latest_calibration embed (null when the kit has no calibration).
export interface StoredKit {
  id: string;
  ownerKind: string; // "testing_company" | "utility_org" | "tester"
  ownerLabel: string;
  manufacturer: string;
  model: string | null;
  serialNumber: string;
  status: string;
  calibratedAt: string | null;
  calibrationExpiresAt: string | null;
}

// The on-device facility row, from BundleFacility. Optional wire address
// members (string | undefined) are normalized to string | null at insert;
// readContext is the read_context map decoded from context_json at read.
// collections/collectionsTruncated are decoded from the SAME context_json
// blob (widened alongside read_context — no new SQLite column, the
// definition_json verbatim-storage precedent). Optional on the TS shape only
// defensively: a snapshot cached before this field shipped decodes to
// undefined for both rather than throwing.
export interface StoredFacility {
  facilityId: string;
  orgId: string;
  orgName: string;
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  stateCode: string | null;
  zipCode: string | null;
  county: string | null;
  pwsId: string | null;
  facilityType: string | null;
  complianceStatus: string | null;
  readContext: Record<string, string>;
  collections?: Record<string, Array<Record<string, string>>>;
  collectionsTruncated?: boolean;
  // Decoded from the same context_json blob; absent on a walk-up's locally
  // synthesized facility, which the bundle never carried.
  devices?: FieldFacilityDeviceRow[];
  pendingNewInstallations?: ChangeRequestPendingNewInstallations;
}

// One requirement as the offline start gate reads it, flattened from the
// bundle's readiness block. `validUntil` is the wire's `valid_until` — the
// calendar day the verdict stops holding (null when it carries no date or did
// not pass) — and the gate compares it with today, which is the whole reason
// it travels onto the device.
export interface StoredRequirementVerdict {
  id: string;
  level: "off" | "review" | "block";
  status: "pass" | "fail" | "not_applicable";
  cure: string;
  validUntil: string | null;
}

// One active hazard type of a utility, as the bundle's utility block lists it.
export interface StoredHazardType {
  id: string;
  label: string;
  sortOrder: number;
}

// One utility's readiness block: the tester's grant status with that org plus
// every requirement evaluated under the org's own settings. `blocked` is the
// server's verdict AT PULL TIME; the gate recomputes it against today rather
// than trusting it.
export interface StoredUtilityRequirements {
  orgId: string;
  orgName: string;
  grantStatus: string;
  blocked: boolean;
  requirements: StoredRequirementVerdict[];
  // The org's change-request evidence levels and IANA zone; absent when the
  // block carried none (the org home's blocks never do).
  changeRequestEvidence?: {
    review: ChangeRequestEvidence[];
    block: ChangeRequestEvidence[];
    reviewWindowDays: number;
  };
  timezone?: string;
  // The vocabulary a violation proposal is recorded in offline, from the
  // bundle's utility block naming this org: its correction window and its
  // active hazard types in the block's order. Both absent when no block named
  // the org — the org home carries none, and the block's fence (an active,
  // company-cleared grant) is narrower than the readiness block's.
  correctionWindowDays?: number;
  hazardTypes?: StoredHazardType[];
}

// The on-device walk-up template row, from BundleWalkupTemplate. orgId is bound
// from the pull scope (the wire row carries no org_id).
export interface StoredWalkupTemplate {
  templateId: string;
  orgId: string;
  templateVersionId: string;
  title: string;
  targetKind: string;
}

// Best-available human label for a bundle kit's owner. The wire carries no
// resolved label: a company-owned kit uses the company name; a utility-org
// kit falls back to its org id absent a resolved name; a self-owned kit uses
// the bare owner_kind ("tester").
export function deriveKitOwnerLabel(k: BundleTestKit): string {
  return k.owner_company_name ?? k.owner_org_id ?? k.owner_kind;
}

export interface StoredDraft {
  assignmentId: string;
  answers: Record<string, unknown>;
  locked: boolean;
  updatedAt: number;
  testKitId: string | null;
}

export type EvidenceKind = "document" | "signature";

export interface DraftAttachmentRow {
  clientAttachmentId: string;
  /**
   * The evidence OWNER: an assignment id for the test form's own files, a
   * staged request's or an inspection's found devices' client set id for a
   * change request's photos, or a staged installation test's client
   * submission id for that test's files.
   */
  assignmentId: string;
  linkId: string;
  kind: EvidenceKind;
  /**
   * Opaque, adapter-owned address of the staged evidence bytes. The storage
   * adapter minted it and is the only thing that can resolve it: a file-system
   * URI on the device, the evidence-store key under IndexedDB. It never
   * reaches the wire — the server addresses evidence by clientAttachmentId.
   */
  locator: string;
  contentType: string;
  /**
   * The picked or captured file's original name — `""` when it has none (a
   * signature). Display only: the wire filename is `<id>.<ext>` (see
   * `evidenceFilename`), and neither this nor `stagedAt` reaches the server.
   */
  fileName: string;
  /** ISO instant the file was attached. */
  stagedAt: string;
}

export interface OutboxSubmissionRow {
  clientSubmissionId: string;
  assignmentId: string;
  orgId: string;
  stack: SyncStack;
  payloadJson: string;
  state: OutboxState;
  serverSubmissionId: string | null;
  finalStatus: string | null;
  attempts: number;
  lastError: string | null;
  /**
   * What the utility refused the document over, retained so the tester can
   * read the cure offline and resubmit once it is cured — through
   * `retryBlocked`, or an amend that rewrites the refused member. Non-null on
   * a `blocked` row only — every other outcome nulls it.
   */
  blockedRequirements: BlockedRequirement[] | null;
  /**
   * The server id of the change-request set that rode this submission, from
   * its `change_request_receipt`; null until then and on a plain test.
   */
  serverChangeRequestSetId: string | null;
  createdAt: number;
  updatedAt: number;
}

export type OutboxAttachmentState = "staged" | "sending" | "uploaded";

/** Which upload route an attachment row takes: the form's or the set's. */
export type AttachmentTarget = "form" | "change_request_set";

/** A change-request file's upload metadata, as the set's upload route takes it. */
export interface ChangeRequestAttachmentRequest {
  kind: ChangeRequestAttachmentKind;
  requestClientId: string | null;
  stagedTestClientSubmissionId: string | null;
}

export interface OutboxAttachmentRow {
  clientAttachmentId: string;
  target: AttachmentTarget;
  // Exactly one owner is non-null: the submission for a form file and for a
  // file of a request riding that submission, the set for a standalone set's.
  clientSubmissionId: string | null;
  clientSetId: string | null;
  linkId: string;
  kind: EvidenceKind;
  /** Adapter-owned staged-bytes address — see DraftAttachmentRow.locator. */
  locator: string;
  contentType: string;
  /** Non-null exactly when `target` is `change_request_set`. */
  request: ChangeRequestAttachmentRequest | null;
  /**
   * The draft row's `fileName` and `stagedAt`, copied at enqueue so a reopened
   * set's files return to the draft as they were attached. Display only.
   */
  fileName: string;
  stagedAt: string;
  state: OutboxAttachmentState;
  attempts: number;
  lastError: string | null;
}

export interface OutboxTransitionRow {
  assignmentId: string;
  orgId: string;
  stack: SyncStack;
  toStatus: WorkAssignmentStatus;
  state: "queued" | "synced";
  createdAt: number;
}

// A queued walk-up assignment create. Enqueued when a tester records a walk-up
// (org-mode-only) so the assignment lands on the server before any submission
// or transition that references it. `stack` is snapshotted "main" at enqueue —
// the CURRENT app mode is irrelevant at replay time (the org-switch hazard) —
// and `payloadJson` is the JSON.stringify of the WorkAssignmentCreateBody
// carrying the client-minted id that makes the replay idempotent.
export interface OutboxAssignmentRow {
  assignmentId: string;
  orgId: string;
  stack: SyncStack;
  payloadJson: string;
  state: "queued" | "sending" | "synced" | "error";
  attempts: number;
  lastError: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface EnqueueAttachmentInput {
  clientAttachmentId: string;
  linkId: string;
  kind: EvidenceKind;
  /** Adapter-owned staged-bytes address — see DraftAttachmentRow.locator. */
  locator: string;
  contentType: string;
  /** The draft row's own two values — see OutboxAttachmentRow.fileName. */
  fileName: string;
  stagedAt: string;
  /** Present on a change request's file: its row targets `change_request_set`. */
  request?: ChangeRequestAttachmentRequest;
}

/** An attachment of a change-request set: its upload metadata is required. */
export type EnqueueSetAttachmentInput = EnqueueAttachmentInput & {
  request: ChangeRequestAttachmentRequest;
};

// An on-device supplement bundle row, flattened from BundleSupplementTemplate.
// deviceCategory is null for a company-wide supplement (the wire omits the
// optional member); the definition is decoded from definition_json at read.
export interface StoredSupplement {
  companyId: string;
  companyName: string;
  deviceCategory: string | null;
  templateId: string;
  templateVersionId: string;
  title: string;
  definition: Record<string, unknown>;
}

// A queued company supplement push. Pushed AFTER its parent regulatory
// submission: the wire body carries parentClientSubmissionId verbatim and the
// server resolves the parent form_submission FK from that client uuid (no
// resolved server id is ever sent). The local parent server-id is consulted
// only as a replay-order gate, never on the wire.
export interface OutboxSupplementRow {
  clientSubmissionId: string;
  parentClientSubmissionId: string;
  companyId: string;
  templateVersionId: string;
  orgId: string;
  stack: SyncStack;
  responsesJson: string;
  state: "finalized" | "sending" | "synced" | "error";
  attempts: number;
  lastError: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface EnqueueSupplementInput {
  clientSubmissionId: string;
  parentClientSubmissionId: string;
  companyId: string;
  templateVersionId: string;
  orgId: string;
  stack: SyncStack;
  responses: Record<string, unknown>;
}

export interface EnqueueSubmissionInput {
  clientSubmissionId: string;
  assignmentId: string;
  orgId: string;
  stack: SyncStack;
  payload: FormSubmissionCreate;
  attachments: EnqueueAttachmentInput[];
}

/** The two payload members a blocked nested submission's cure replaces. */
export type SubmissionRequestMembers = Required<
  Pick<FormSubmissionCreate, "device_of_record" | "change_requests">
>;

export type OutboxChangeRequestSetState =
  | "queued"
  | "needs_answer"
  | "sending"
  | "uploading_evidence"
  | "received"
  | "blocked"
  | "error"
  | "synced";

// A queued standalone / assignment / intake change-request set, replayed on
// the field stack (the only stack that carries sets). `bodyJson` is the
// JSON.stringify of the ChangeRequestSetCreate; the create is idempotent on its
// client_set_id, which is also this row's key (UUIDv7, so key order is FIFO).
export interface OutboxChangeRequestSetRow {
  clientSetId: string;
  bodyJson: string;
  state: OutboxChangeRequestSetState;
  /** The server's receipt (set id + evidence manifest); null until the create lands. */
  receipt: ChangeRequestReceipt | null;
  /** The at-send candidates; non-null only while the row is `needs_answer`. */
  candidates: IntakeCandidate[] | null;
  /** Epoch ms of the last failed at-send candidate check; a successful one clears it. */
  lastCheckFailedAt: number | null;
  /**
   * What the utility blocked the set on — a request's missing evidence, or a
   * staged test's unmet requirement with its cure; non-null only while the row
   * is `blocked`.
   */
  blockedRequirements: BlockedRequirement[] | null;
  attempts: number;
  lastError: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface EnqueueChangeRequestSetInput {
  clientSetId: string;
  body: ChangeRequestSetCreate;
  attachments: EnqueueSetAttachmentInput[];
}

/**
 * The tester's answer to at-send candidates: one facility picked, or none of
 * them, with the optional "why not these?" written on each dismissed one.
 */
export type IntakeCandidatesAnswer =
  | { kind: "pick"; facilityId: string }
  | { kind: "none"; reason?: string };

// One staged installation test's in-progress answers, under the client
// submission id its link carries (also the owner its files stage under). The
// saved request-side members ride the request item's `staged_tests`.
export interface SidecarStagedTest {
  clientSubmissionId: string;
  answers: Record<string, unknown>;
  /** The draft whose readings this link copied (Before my visit's reuse); absent on a fresh test. */
  copiedFrom?: string;
}

// A request staged beside a test: the built item plus the client set id its
// photos stage under (minted when the request is staged — never the
// assignment id, whose draft reloads every photo filed under it into the form)
// and the device editor's state it restores from (null for a removal).
export interface SidecarStagedRequest {
  clientSetId: string;
  item: ChangeRequestItemCreate;
  deviceInput: DeviceInputState | null;
  stagedTests: SidecarStagedTest[];
}

/** The rejected test a returned request travels back with, as History pinned it. */
export interface PinnedRejection {
  submissionId: string;
  performedAt: string;
  rejectedOn: string;
  reasonCode: components["schemas"]["RejectionReasonCode"] | null;
  reasonText: string | null;
  // Absent from the bundle row a pre-staged returned card is pinned from.
  outcome: components["schemas"]["FormSubmissionOutcome"] | null;
}

/**
 * The `assignment` set a test's panel sent. `syncMetaAtSync` is the server
 * sync meta stamped by the first reader that finds the set's row synced or
 * gone — the one clock its pending state is judged on.
 */
export interface SentSet {
  clientSetId: string;
  kind: ChangeRequestKind;
  syncMetaAtSync?: string;
}

/** The facility a standalone draft is locked to. */
export interface DraftSetFacility {
  id: string;
  name: string;
  addressLine: string;
  orgName: string;
}

/** The intake route's steps and the standalone Replace route's. */
export type DraftSetStep = "utility" | "facility" | "assemblies" | "assembly" | "test" | "review";

// One unsent device request: every draft-set item comes from a device editor,
// so its state is always there to restore from.
export interface DraftSetItem {
  item: ChangeRequestItemCreate;
  deviceInput: DeviceInputState;
  stagedTests: SidecarStagedTest[];
}

// The devices an inspection found on site, each a New installation riding the
// inspection's own create — on a resubmission, first the ones the rejected
// inspection returned, which the pre-stage seeds under a new set and marks with
// `seededFrom`, that rejected submission's id. `clientSetId` is minted with the
// first entry and owns every photo the entries name (each staged with its
// entry's client request id as link id) — never the assignment id, whose draft
// reloads every photo filed under it into the form. A seeded set the tester
// empties is still sent, as an empty set: the redo proposes no found devices.
export interface SidecarFoundDevices {
  clientSetId: string;
  items: DraftSetItem[];
  seededFrom?: string;
}

// The violations an inspection found, each riding the inspection's own create
// as one `violation_proposals` item — on a resubmission, first the ones the
// rejected inspection returned, which the pre-stage seeds and marks with
// `seededFrom`, that rejected submission's id. A proposal stages no evidence,
// so the member names no client set id and no owner for
// `sidecarEvidenceOwners`. A seeded list the tester empties is still sent, as
// the empty array: the redo proposes no violations.
export interface SidecarViolationProposals {
  items: ViolationProposalCreate[];
  seededFrom?: string;
}

/** The rejected request a draft resubmits, pinned on top of its editor. */
export interface DraftSetResubmission {
  requestId: string;
  kind: ChangeRequestKind;
  reasonCode: ChangeRequestRejectionReason;
  reasonText: string | null;
  cure: string | null;
}

interface DraftSetBase {
  kind: "draft_set";
  step: DraftSetStep;
  arrivedViaLetterCode: string | null;
  items: DraftSetItem[];
  resubmission: DraftSetResubmission | null;
}

/**
 * A draft's panel state beside its answer document, which stays unchanged
 * (extra keys would fail document validation). Keyed by an assignment id for a
 * test's panel or an inspection's found devices and violations, or by a client
 * set id for an unsent standalone / intake set.
 * Opaque JSON to both adapters, except the evidence owners an acknowledged
 * test's or received set's cleanup deletes — see `sidecarEvidenceOwners`.
 *
 * A draft set is one arm per anchor: an intake carries its (partial) facts and
 * an org only once the Utility step picked one; a standalone set is locked to
 * a facility in a known org, with the predecessor's bases when it replaces one.
 */
export type DraftSidecar =
  | {
      kind: "assignment";
      deviceOfRecord: DeviceOfRecordAttestation | null;
      // When the tester answered the device-of-record question (ISO).
      answeredAt: string | null;
      stagedRequest: SidecarStagedRequest | null;
      removalSetAside: boolean;
      pinnedRejection: PinnedRejection | null;
      sentSet: SentSet | null;
      // An inspection's found devices; absent means none. Optional because
      // a device test's panel never stages one.
      foundDevices?: SidecarFoundDevices;
      // An inspection's violations found; absent means none, optional for
      // the same reason.
      violationProposals?: SidecarViolationProposals;
    }
  | (DraftSetBase & {
      anchor: "intake";
      orgId: string | null;
      intake: Partial<IntakeFacts>;
    })
  | (DraftSetBase & {
      anchor: "standalone";
      orgId: string;
      facility: DraftSetFacility;
      recordBase: DeviceRecordBase | null;
    });

export interface StoredDraftSidecar {
  key: string;
  sidecar: DraftSidecar;
}

/**
 * Every evidence owner a sidecar names beyond its own key: an assignment
 * sidecar's staged request client set id and each staged test's client
 * submission id, and its found devices' client set id; a set draft's staged
 * tests' client submission ids. A sent test's or received set's cleanup
 * deletes the rows and bytes under each of these before the sidecar, so
 * nothing staged for it outlives it.
 */
export function sidecarEvidenceOwners(sidecar: DraftSidecar): string[] {
  if (sidecar.kind === "draft_set") {
    return sidecar.items.flatMap((i) => i.stagedTests.map((t) => t.clientSubmissionId));
  }
  const request = sidecar.stagedRequest;
  const owners =
    request === null
      ? []
      : [request.clientSetId, ...request.stagedTests.map((t) => t.clientSubmissionId)];
  return sidecar.foundDevices === undefined
    ? owners
    : [...owners, sidecar.foundDevices.clientSetId];
}

/** One stored catalog's metadata, as `list()` reports it. */
export interface StoredCatalogInfo {
  orgId: string;
  /** The RAW token (the bundle's `catalog_versions` value), never the quoted header. */
  etag: string;
  /** UTF-8 bytes of the stored document JSON. */
  byteSize: number;
  savedAt: number;
}
